/**
 * Demo data for the console. There is no backend yet, so this module
 * simulates a week of location history for two accounts ("fleets") and
 * derives everything else (current position, stops, trips, alerts) from it,
 * which keeps the pages consistent with each other: the alert that says
 * "Carry-on left Home" points at a real departure in the Carry-on trail.
 *
 * Times are relative to page load (NOW), so the demo always looks current.
 * Randomness is seeded per device and day, so a reload looks the same.
 *
 * Swap point: replace FLEET_DATA with API calls once devices report in.
 */
import { formatDuration } from '@/lib/format';
import { CARD_PRICE_AMOUNT, CLOUD_SYNC_MINUTES } from '@/lib/product';
import { offsetMeters, pathLength, pointAlong, type LatLng } from '@/lib/geo';

export const NOW = Date.now();
const MIN = 60_000;
const HOUR = 60 * MIN;

export const HISTORY_DAYS = 7;

/** Local midnight `offset` days from today (DST-safe). */
export function dayStart(offset: number): number {
  const d = new Date(NOW);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.getTime();
}

export const TODAY_START = dayStart(0);
export const HISTORY_START = dayStart(-(HISTORY_DAYS - 1));

// ─── Types ──────────────────────────────────────────────────────────

export type FleetId = 'personal' | 'acme';
export type FixSource = 'Phone' | 'Gateway';
export type PingMode = 'standard' | 'saver';
export type DeviceKind =
  | 'wallet'
  | 'luggage'
  | 'backpack'
  | 'bike'
  | 'toolbox'
  | 'van'
  | 'level'
  | 'generator'
  | 'survey';
export type DeviceStatus = 'moving' | 'still' | 'offline';
export type TravelMode = 'walk' | 'bike' | 'transit' | 'drive';

export interface Fix extends LatLng {
  t: number;
  source: FixSource;
  moving: boolean;
}

export interface Stay extends LatLng {
  id: string;
  from: number;
  to: number;
  label: string;
  placeId?: string;
  /** Still there now. */
  ongoing: boolean;
}

export interface Trip {
  id: string;
  from: number;
  to: number;
  distanceM: number;
  mode: TravelMode;
  fromLabel: string;
  toLabel: string;
  /** Still moving now. */
  ongoing: boolean;
}

export interface Device {
  id: string;
  fleetId: FleetId;
  name: string;
  kind: DeviceKind;
  serial: string;
  firmware: string;
  battery: number;
  pingMode: PingMode;
  /** Illustrative finder reception, 0-4 bars; not exposed as tag telemetry. */
  signalBars: number;
  status: DeviceStatus;
  lastFix: Fix;
  locationLabel: string;
  fixes: Fix[];
  stays: Stay[];
  trips: Trip[];
  activatedAt: number;
  dataUsedMb: number;
}

export interface PlaceRule {
  deviceId: string;
  onEnter: boolean;
  onLeave: boolean;
  /** Legacy demo setting; not a supported phone-separation feature. */
  onlyWhenAway?: boolean;
}

export interface Place extends LatLng {
  id: string;
  fleetId: FleetId;
  name: string;
  area: string;
  radiusM: number;
  rules: PlaceRule[];
}

export type AlertKind = 'movement' | 'battery' | 'place' | 'device';

export interface Alert {
  id: string;
  fleetId: FleetId;
  kind: AlertKind;
  deviceId: string;
  placeId?: string;
  title: string;
  detail: string;
  at: number;
  /** Older alerts start out read. */
  defaultRead: boolean;
}

export interface Fleet {
  id: FleetId;
  name: string;
  kind: 'personal' | 'team';
  members: number;
}

export interface Order {
  id: string;
  date: number;
  cards: number;
  amount: number;
}

// ─── Deterministic randomness ───────────────────────────────────────

function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

type Rng = () => number;

function rngFor(...parts: (string | number)[]): Rng {
  let a = hash(parts.join(':'));
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rng: Rng, min: number, max: number) => min + (max - min) * rng();
/** Roughly bell-shaped, in -1..1. */
const wobble = (rng: Rng) => (rng() + rng() + rng() - 1.5) / 1.5;

/** A clock time on day `offset`, optionally jittered by up to plus or minus jitterMin. */
function clock(offset: number, hhmm: string, rng?: Rng, jitterMin = 0): number {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(dayStart(offset));
  d.setHours(h, m, 0, 0);
  const jitter = rng && jitterMin ? Math.round((rng() * 2 - 1) * jitterMin) : 0;
  return d.getTime() + jitter * MIN;
}

const isWeekend = (offset: number) => {
  const day = new Date(dayStart(offset)).getDay();
  return day === 0 || day === 6;
};

// ─── Locations ──────────────────────────────────────────────────────

interface Loc extends LatLng {
  label: string;
  placeId?: string;
  indoor?: boolean;
}

const loc = (lat: number, lng: number, label: string, extra: Partial<Loc> = {}): Loc => ({
  lat,
  lng,
  label,
  ...extra,
});

const ll = (lat: number, lng: number): LatLng => ({ lat, lng });

// Personal: a family in Noe Valley, San Francisco.
const P = {
  home: loc(37.75021, -122.43372, 'Home', { placeId: 'home', indoor: true }),
  garage: loc(37.75006, -122.43401, 'Garage', { placeId: 'garage', indoor: true }),
  office: loc(37.78655, -122.39668, 'Office', { placeId: 'office', indoor: true }),
  school: loc(37.75632, -122.42234, 'School', { placeId: 'school', indoor: true }),
  bart24: loc(37.7522, -122.41842, '24th St Mission BART'),
  montgomery: loc(37.78939, -122.40131, 'Montgomery St BART'),
  blueBottle: loc(37.78262, -122.40748, 'Blue Bottle, Mint Plaza', { indoor: true }),
  rainbow: loc(37.76937, -122.4153, 'Rainbow Grocery, Folsom St', { indoor: true }),
  dolores: loc(37.75962, -122.4269, 'Dolores Park'),
  tartine: loc(37.76143, -122.4241, 'Tartine, Guerrero St', { indoor: true }),
  library: loc(37.75055, -122.4194, 'Mission Library, Bartlett St', { indoor: true }),
  ferry: loc(37.79552, -122.3937, 'Ferry Building'),
  sanchez: loc(37.7509, -122.4329, 'Sanchez St'),
};

const R = {
  homeToBart: [ll(37.75165, -122.4333), ll(37.75195, -122.4278), ll(37.75215, -122.422)],
  bartToMontgomery: [ll(37.76505, -122.41964), ll(37.7797, -122.41392), ll(37.7844, -122.40797)],
  montgomeryToOffice: [ll(37.788, -122.3998), ll(37.7872, -122.398)],
  officeToBlueBottle: [ll(37.7851, -122.4004), ll(37.7833, -122.4052)],
  blueBottleToOffice: [ll(37.7833, -122.4052), ll(37.7851, -122.4004)],
  officeToRainbow: [ll(37.783, -122.401), ll(37.777, -122.4085), ll(37.7725, -122.414)],
  rainbowToHome: [ll(37.765, -122.419), ll(37.7585, -122.4205), ll(37.7519, -122.425), ll(37.75165, -122.4333)],
  homeToDolores: [ll(37.7512, -122.4335), ll(37.7518, -122.4288), ll(37.757, -122.4282)],
  doloresToTartine: [ll(37.761, -122.426)],
  tartineToHome: [ll(37.7605, -122.4243), ll(37.756, -122.424), ll(37.7518, -122.4288), ll(37.7512, -122.4335)],
  doloresToHome: [ll(37.757, -122.4282), ll(37.7518, -122.4288), ll(37.7512, -122.4335)],
  homeToSchool: [ll(37.7516, -122.433), ll(37.752, -122.426), ll(37.755, -122.423)],
  schoolToLibrary: [ll(37.754, -122.4205), ll(37.751, -122.4198)],
  libraryToHome: [ll(37.752, -122.4215), ll(37.7519, -122.428), ll(37.7516, -122.433)],
  homeToFerry: [
    ll(37.7512, -122.4335),
    ll(37.7645, -122.4315),
    ll(37.771, -122.424),
    ll(37.777, -122.417),
    ll(37.7835, -122.409),
    ll(37.79, -122.4),
    ll(37.794, -122.3955),
  ],
  garageToFerry: [
    ll(37.7515, -122.4331),
    ll(37.762, -122.4312),
    ll(37.77, -122.425),
    ll(37.777, -122.417),
    ll(37.7835, -122.409),
    ll(37.7905, -122.3995),
  ],
  ferryToGarage: [
    ll(37.7905, -122.3995),
    ll(37.773, -122.422),
    ll(37.765, -122.4218),
    ll(37.758, -122.4213),
    ll(37.752, -122.4258),
    ll(37.7515, -122.4331),
  ],
};

// Acme Field Ops: a small contractor with a yard on Bayshore and two job sites.
const A = {
  yard: loc(37.7423, -122.4051, 'Yard', { placeId: 'yard' }),
  mission: loc(37.7905, -122.3986, '450 Mission site', { placeId: 'mission-site', indoor: true }),
  dogpatch: loc(37.7592, -122.3881, 'Dogpatch site', { placeId: 'dogpatch-site' }),
};

const AR = {
  yardToMission: [ll(37.748, -122.405), ll(37.76, -122.4045), ll(37.77, -122.405), ll(37.778, -122.399), ll(37.786, -122.397)],
  missionToDogpatch: [ll(37.783, -122.391), ll(37.773, -122.3895), ll(37.765, -122.389)],
  dogpatchToYard: [ll(37.754, -122.391), ll(37.748, -122.397), ll(37.744, -122.402)],
  yardToDogpatch: [ll(37.744, -122.402), ll(37.748, -122.397), ll(37.754, -122.391)],
};

// ─── Simulation ─────────────────────────────────────────────────────

interface StaySeg {
  kind: 'stay';
  loc: Loc;
  from: number;
  to: number;
}

interface MoveSeg {
  kind: 'move';
  from: number;
  to: number;
  path: LatLng[];
  mode: TravelMode;
  underground: boolean;
  fromLoc: Loc;
  toLoc: Loc;
}

/** Builds a device's week as a chain of stays and moves. */
class Timeline {
  segs: (StaySeg | MoveSeg)[] = [];
  t: number;
  loc: Loc;

  constructor(t: number, start: Loc) {
    this.t = t;
    this.loc = start;
  }

  stayUntil(t: number): this {
    if (t <= this.t) return this;
    const last = this.segs[this.segs.length - 1];
    if (last?.kind === 'stay' && last.loc === this.loc) last.to = t;
    else this.segs.push({ kind: 'stay', loc: this.loc, from: this.t, to: t });
    this.t = t;
    return this;
  }

  stayFor(minutes: number): this {
    return this.stayUntil(this.t + minutes * MIN);
  }

  moveTo(to: Loc, via: LatLng[], minutes: number, mode: TravelMode, underground = false): this {
    const end = this.t + Math.round(minutes * MIN);
    this.segs.push({
      kind: 'move',
      from: this.t,
      to: end,
      path: [this.loc, ...via, to],
      mode,
      underground,
      fromLoc: this.loc,
      toLoc: to,
    });
    this.t = end;
    this.loc = to;
    return this;
  }
}

/** Reports at the supported cloud sync intervals. */
const STILL_INTERVAL: Record<PingMode, number> = { standard: CLOUD_SYNC_MINUTES[0], saver: CLOUD_SYNC_MINUTES[1] };
const MOVING_INTERVAL: Record<PingMode, number> = { standard: CLOUD_SYNC_MINUTES[0], saver: CLOUD_SYNC_MINUTES[1] };

interface DeviceSpec {
  id: string;
  fleetId: FleetId;
  name: string;
  kind: DeviceKind;
  serial: string;
  firmware: string;
  battery: number;
  pingMode: PingMode;
  signalBars: number;
  start: Loc;
  activatedDaysAgo: number;
  dataUsedMb: number;
  /** A gateway supplies reports for this sample. */
  gatewayOnly?: boolean;
  /** The card stopped reporting at this time. */
  offlineAfter?: number;
  build: (tl: Timeline) => void;
}

function stillFix(at: Loc, t: number, rng: Rng, gatewayOnly: boolean): Fix {
  const source: FixSource = gatewayOnly || (at.indoor && rng() < 0.5) ? 'Gateway' : 'Phone';
  const p = offsetMeters(at, wobble(rng) * 35, wobble(rng) * 35);
  return { t, lat: p.lat, lng: p.lng, source, moving: false };
}

function movingFix(p: LatLng, t: number, rng: Rng, underground: boolean): Fix {
  const source: FixSource = underground ? 'Gateway' : 'Phone';
  const q = offsetMeters(p, wobble(rng) * 35, wobble(rng) * 35);
  return { t, lat: q.lat, lng: q.lng, source, moving: true };
}

function dominantMode(modes: Set<TravelMode>): TravelMode {
  if (modes.has('drive')) return 'drive';
  if (modes.has('transit')) return 'transit';
  if (modes.has('bike')) return 'bike';
  return 'walk';
}

function simulate(spec: DeviceSpec): Device {
  const tl = new Timeline(HISTORY_START, spec.start);
  spec.build(tl);
  tl.stayUntil(NOW + MIN);

  const end = spec.offlineAfter ?? NOW;
  const reporting = spec.offlineAfter === undefined;
  const fixes: Fix[] = [];
  const stays: Stay[] = [];
  const trips: Trip[] = [];

  let trip: {
    from: number;
    to: number;
    distanceM: number;
    modes: Set<TravelMode>;
    fromLabel: string;
    toLabel: string;
    ongoing: boolean;
  } | null = null;

  const closeTrip = () => {
    if (!trip) return;
    trips.push({
      id: `${spec.id}-trip-${trip.from}`,
      from: trip.from,
      to: trip.to,
      distanceM: Math.round(trip.distanceM),
      mode: dominantMode(trip.modes),
      fromLabel: trip.fromLabel,
      toLabel: trip.toLabel,
      ongoing: trip.ongoing,
    });
    trip = null;
  };

  for (const seg of tl.segs) {
    if (seg.from >= end) break;
    const segEnd = Math.min(seg.to, end);
    const rng = rngFor(spec.id, seg.from);

    if (seg.kind === 'stay') {
      closeTrip();
      stays.push({
        id: `${spec.id}-stay-${seg.from}`,
        lat: seg.loc.lat,
        lng: seg.loc.lng,
        from: seg.from,
        to: segEnd,
        label: seg.loc.label,
        placeId: seg.loc.placeId,
        ongoing: reporting && seg.to > end,
      });
      const interval = STILL_INTERVAL[spec.pingMode] * MIN;
      let t = seg.from;
      while (t <= segEnd) {
        fixes.push(stillFix(seg.loc, t, rng, !!spec.gatewayOnly));
        let next = Math.ceil((t + 1) / interval) * interval;
        if (next - t < MIN) next += interval;
        t = next;
      }
    } else {
      const interval = MOVING_INTERVAL[spec.pingMode] * MIN;
      const duration = seg.to - seg.from;
      for (let t = seg.from; t <= segEnd; t += interval) {
        fixes.push(movingFix(pointAlong(seg.path, (t - seg.from) / duration), t, rng, seg.underground));
      }
      const distance = pathLength(seg.path) * ((segEnd - seg.from) / duration);
      if (!trip) {
        trip = {
          from: seg.from,
          to: segEnd,
          distanceM: 0,
          modes: new Set(),
          fromLabel: seg.fromLoc.label,
          toLabel: seg.toLoc.label,
          ongoing: false,
        };
      }
      trip.to = segEnd;
      trip.distanceM += distance;
      trip.modes.add(seg.mode);
      trip.toLabel = seg.toLoc.label;
      trip.ongoing = reporting && seg.to > end;
    }
  }
  closeTrip();

  const current = tl.segs.find((s) => s.from <= end && s.to > end);
  const status: DeviceStatus = !reporting ? 'offline' : current?.kind === 'move' ? 'moving' : 'still';
  const lastStay = stays[stays.length - 1];
  const lastTrip = trips[trips.length - 1];
  const locationLabel =
    status === 'offline'
      ? `Near ${lastStay?.label ?? 'unknown location'}`
      : status === 'moving'
        ? `In transit from ${lastTrip?.fromLabel ?? 'unknown'}`
        : (lastStay?.label ?? 'Unknown');

  return {
    id: spec.id,
    fleetId: spec.fleetId,
    name: spec.name,
    kind: spec.kind,
    serial: spec.serial,
    firmware: spec.firmware,
    battery: spec.battery,
    pingMode: spec.pingMode,
    signalBars: reporting ? spec.signalBars : 0,
    status,
    lastFix: fixes[fixes.length - 1],
    locationLabel,
    fixes,
    stays,
    trips,
    activatedAt: dayStart(-spec.activatedDaysAgo),
    dataUsedMb: spec.dataUsedMb,
  };
}

// ─── Devices ────────────────────────────────────────────────────────

/** When the bike was moved out of the garage, for its alert. */
const BIKE_INCIDENT_AT = NOW - 200 * MIN;
const TOOLBOX_OFFLINE_AT = NOW - 2 * 24 * HOUR - 3 * HOUR - 17 * MIN;

const PERSONAL_SPECS: DeviceSpec[] = [
  {
    id: 'wallet',
    fleetId: 'personal',
    name: 'Wallet',
    kind: 'wallet',
    serial: 'LTS-4K7Q-29MP',
    firmware: '1.4.2',
    battery: 15,
    pingMode: 'standard',
    signalBars: 3,
    start: P.home,
    activatedDaysAgo: 132,
    dataUsedMb: 3.4,
    build: (tl) => {
      for (let d = -6; d <= 0; d++) {
        const rng = rngFor('wallet', dayStart(d));
        if (isWeekend(d)) {
          tl.stayUntil(clock(d, '10:40', rng, 20))
            .moveTo(P.dolores, R.homeToDolores, 16, 'walk')
            .stayUntil(clock(d, '13:15', rng, 15))
            .moveTo(P.tartine, R.doloresToTartine, 5, 'walk')
            .stayFor(between(rng, 22, 40))
            .moveTo(P.home, R.tartineToHome, 18, 'walk');
        } else {
          tl.stayUntil(clock(d, '08:12', rng, 8))
            .moveTo(P.bart24, R.homeToBart, 12, 'walk')
            .moveTo(P.montgomery, R.bartToMontgomery, 17, 'transit', true)
            .moveTo(P.office, R.montgomeryToOffice, 7, 'walk')
            .stayUntil(clock(d, '12:25', rng, 10))
            .moveTo(P.blueBottle, R.officeToBlueBottle, 13, 'walk')
            .stayUntil(clock(d, '13:05', rng, 8))
            .moveTo(P.office, R.blueBottleToOffice, 13, 'walk')
            .stayUntil(clock(d, '17:40', rng, 15))
            .moveTo(P.rainbow, R.officeToRainbow, 22, 'transit')
            .stayFor(between(rng, 18, 35))
            .moveTo(P.home, R.rainbowToHome, 26, 'transit');
        }
      }
    },
  },
  {
    id: 'carry-on',
    fleetId: 'personal',
    name: 'Carry-on',
    kind: 'luggage',
    serial: 'LTS-8T2W-61QA',
    firmware: '1.4.2',
    battery: 81,
    pingMode: 'standard',
    signalBars: 4,
    start: P.home,
    activatedDaysAgo: 61,
    dataUsedMb: 0.9,
    build: (tl) => {
      // Packed and left about 18 minutes ago; still on the road.
      tl.stayUntil(NOW - 18 * MIN).moveTo(P.ferry, R.homeToFerry, 34, 'drive');
    },
  },
  {
    id: 'backpack',
    fleetId: 'personal',
    name: "Maya's backpack",
    kind: 'backpack',
    serial: 'LTS-3N9C-74RK',
    firmware: '1.4.2',
    battery: 64,
    pingMode: 'standard',
    signalBars: 3,
    start: P.home,
    activatedDaysAgo: 98,
    dataUsedMb: 2.7,
    build: (tl) => {
      for (let d = -6; d <= 0; d++) {
        const rng = rngFor('backpack', dayStart(d));
        if (isWeekend(d)) {
          tl.stayUntil(clock(d, '14:00', rng, 30))
            .moveTo(P.dolores, R.homeToDolores, 17, 'walk')
            .stayUntil(clock(d, '16:10', rng, 20))
            .moveTo(P.home, R.doloresToHome, 17, 'walk');
        } else {
          tl.stayUntil(clock(d, '07:52', rng, 5))
            .moveTo(P.school, R.homeToSchool, 18, 'walk')
            .stayUntil(clock(d, '15:05', rng, 5))
            .moveTo(P.library, R.schoolToLibrary, 9, 'walk')
            .stayUntil(clock(d, '16:30', rng, 12))
            .moveTo(P.home, R.libraryToHome, 19, 'walk');
        }
      }
    },
  },
  {
    id: 'bike',
    fleetId: 'personal',
    name: 'Bike',
    kind: 'bike',
    serial: 'LTS-6H1D-08ZE',
    firmware: '1.4.1',
    battery: 47,
    pingMode: 'standard',
    signalBars: 2,
    start: P.garage,
    activatedDaysAgo: 210,
    dataUsedMb: 1.1,
    build: (tl) => {
      for (const d of [-5, -2]) {
        const rng = rngFor('bike', dayStart(d));
        tl.stayUntil(clock(d, '09:30', rng, 10))
          .moveTo(P.ferry, R.garageToFerry, 27, 'bike')
          .stayUntil(clock(d, '10:55', rng, 10))
          .moveTo(P.garage, R.ferryToGarage, 31, 'bike');
      }
      tl.stayUntil(BIKE_INCIDENT_AT)
        .moveTo(P.sanchez, [], 3, 'walk')
        .stayFor(6)
        .moveTo(P.garage, [], 3, 'walk');
    },
  },
  {
    id: 'toolbox',
    fleetId: 'personal',
    name: 'Toolbox',
    kind: 'toolbox',
    serial: 'LTS-2F5J-93LV',
    firmware: '1.3.9',
    battery: 9,
    pingMode: 'saver',
    signalBars: 1,
    start: P.garage,
    activatedDaysAgo: 240,
    dataUsedMb: 0.3,
    gatewayOnly: true,
    offlineAfter: TOOLBOX_OFFLINE_AT,
    build: () => {},
  },
];

const ACME_SPECS: DeviceSpec[] = [
  {
    id: 'van-kit',
    fleetId: 'acme',
    name: 'Van 12 kit',
    kind: 'van',
    serial: 'LTS-9A4E-15GH',
    firmware: '1.4.2',
    battery: 73,
    pingMode: 'standard',
    signalBars: 4,
    start: A.yard,
    activatedDaysAgo: 45,
    dataUsedMb: 4.8,
    build: (tl) => {
      for (let d = -6; d <= 0; d++) {
        if (isWeekend(d)) continue;
        const rng = rngFor('van-kit', dayStart(d));
        tl.stayUntil(clock(d, '07:10', rng, 8))
          .moveTo(A.mission, AR.yardToMission, 26, 'drive')
          .stayUntil(clock(d, '11:30', rng, 15))
          .moveTo(A.dogpatch, AR.missionToDogpatch, 16, 'drive')
          .stayUntil(clock(d, '15:20', rng, 15))
          .moveTo(A.yard, AR.dogpatchToYard, 19, 'drive');
      }
    },
  },
  {
    id: 'laser-level',
    fleetId: 'acme',
    name: 'Laser level LL-3',
    kind: 'level',
    serial: 'LTS-5M8P-40TX',
    firmware: '1.4.2',
    battery: 58,
    pingMode: 'saver',
    signalBars: 2,
    start: A.mission,
    activatedDaysAgo: 45,
    dataUsedMb: 0.6,
    build: () => {},
  },
  {
    id: 'generator',
    fleetId: 'acme',
    name: 'Generator G2',
    kind: 'generator',
    serial: 'LTS-7R3B-26NC',
    firmware: '1.4.0',
    battery: 22,
    pingMode: 'standard',
    signalBars: 3,
    start: A.dogpatch,
    activatedDaysAgo: 38,
    dataUsedMb: 1.9,
    build: () => {},
  },
  {
    id: 'survey-kit',
    fleetId: 'acme',
    name: 'Survey kit 04',
    kind: 'survey',
    serial: 'LTS-1W6K-57DS',
    firmware: '1.4.2',
    battery: 91,
    pingMode: 'standard',
    signalBars: 4,
    start: A.yard,
    activatedDaysAgo: 30,
    dataUsedMb: 2.2,
    build: (tl) => {
      for (let d = -6; d <= -1; d++) {
        if (isWeekend(d)) continue;
        const rng = rngFor('survey-kit', dayStart(d));
        tl.stayUntil(clock(d, '08:00', rng, 10))
          .moveTo(A.dogpatch, AR.yardToDogpatch, 17, 'drive')
          .stayUntil(clock(d, '12:00', rng, 20))
          .moveTo(A.yard, AR.dogpatchToYard, 19, 'drive');
      }
      // Today: out to Dogpatch, and on its way back right now.
      tl.stayUntil(Math.min(clock(0, '08:05'), NOW - 95 * MIN))
        .moveTo(A.dogpatch, AR.yardToDogpatch, 17, 'drive')
        .stayUntil(NOW - 6 * MIN)
        .moveTo(A.yard, AR.dogpatchToYard, 20, 'drive');
    },
  },
];

// ─── Places ─────────────────────────────────────────────────────────

const PERSONAL_PLACES: Place[] = [
  {
    id: 'home',
    fleetId: 'personal',
    name: 'Home',
    area: 'Noe Valley',
    lat: P.home.lat,
    lng: P.home.lng,
    radiusM: 75,
    rules: [
      { deviceId: 'carry-on', onEnter: false, onLeave: true },
      { deviceId: 'backpack', onEnter: true, onLeave: true },
    ],
  },
  {
    id: 'school',
    fleetId: 'personal',
    name: 'School',
    area: 'Mission District',
    lat: P.school.lat,
    lng: P.school.lng,
    radiusM: 100,
    rules: [{ deviceId: 'backpack', onEnter: true, onLeave: true }],
  },
  {
    id: 'office',
    fleetId: 'personal',
    name: 'Office',
    area: '2nd St, SoMa',
    lat: P.office.lat,
    lng: P.office.lng,
    radiusM: 120,
    rules: [],
  },
  {
    id: 'garage',
    fleetId: 'personal',
    name: 'Garage',
    area: 'Noe Valley',
    lat: P.garage.lat,
    lng: P.garage.lng,
    radiusM: 25,
    rules: [
      { deviceId: 'bike', onEnter: false, onLeave: true },
      { deviceId: 'toolbox', onEnter: false, onLeave: true },
    ],
  },
];

const ACME_PLACES: Place[] = [
  {
    id: 'yard',
    fleetId: 'acme',
    name: 'Yard',
    area: 'Bayshore Blvd',
    lat: A.yard.lat,
    lng: A.yard.lng,
    radiusM: 150,
    rules: [
      { deviceId: 'van-kit', onEnter: false, onLeave: true },
      { deviceId: 'survey-kit', onEnter: true, onLeave: true },
    ],
  },
  {
    id: 'mission-site',
    fleetId: 'acme',
    name: '450 Mission site',
    area: 'Financial District',
    lat: A.mission.lat,
    lng: A.mission.lng,
    radiusM: 90,
    rules: [
      { deviceId: 'van-kit', onEnter: true, onLeave: false },
      { deviceId: 'laser-level', onEnter: false, onLeave: true },
    ],
  },
  {
    id: 'dogpatch-site',
    fleetId: 'acme',
    name: 'Dogpatch site',
    area: 'Illinois St, Dogpatch',
    lat: A.dogpatch.lat,
    lng: A.dogpatch.lng,
    radiusM: 120,
    rules: [
      { deviceId: 'generator', onEnter: false, onLeave: true },
      { deviceId: 'survey-kit', onEnter: true, onLeave: true },
    ],
  },
];

// ─── Alerts ─────────────────────────────────────────────────────────

const READ_AFTER = 12 * HOUR;

/** Routine alerts (a school arrival on a school day) start out read. */
function alert(a: Omit<Alert, 'defaultRead'> & { routine?: boolean }): Alert {
  const { routine, ...rest } = a;
  return { ...rest, defaultRead: !!routine || NOW - a.at > READ_AFTER };
}

const lastStayAt = (device: Device, placeId: string, ended = false) =>
  [...device.stays].reverse().find((s) => s.placeId === placeId && (!ended || !s.ongoing));

function personalAlerts(devices: Record<string, Device>): Alert[] {
  const out: Alert[] = [];
  const carry = devices['carry-on'];
  const departure = carry.trips[carry.trips.length - 1];
  if (departure) {
    out.push(
      alert({
        id: 'carry-on-left-home',
        fleetId: 'personal',
        kind: 'place',
        deviceId: 'carry-on',
        placeId: 'home',
        title: 'Carry-on left Home',
        detail: 'Seen near Market St.',
        at: departure.from,
      }),
    );
  }

  out.push(
    alert({
      id: 'bike-moved-garage',
      fleetId: 'personal',
      kind: 'place',
      deviceId: 'bike',
      placeId: 'garage',
      title: 'Bike seen outside Garage',
      detail: 'Seen near Sanchez St., then Garage.',
      at: BIKE_INCIDENT_AT,
    }),
  );


  const backpack = devices.backpack;
  const atSchool = lastStayAt(backpack, 'school');
  if (atSchool) {
    const walk = backpack.trips.find((t) => t.to === atSchool.from);
    out.push(
      alert({
        id: `backpack-arrived-school-${atSchool.from}`,
        fleetId: 'personal',
        kind: 'place',
        deviceId: 'backpack',
        placeId: 'school',
        title: "Maya's backpack arrived at School",
        routine: true,
        detail: walk ? `From ${walk.fromLabel}, a ${formatDuration(walk.to - walk.from)} walk.` : 'Inside the School zone.',
        at: atSchool.from,
      }),
    );
  }
  const leftSchool = lastStayAt(backpack, 'school', true);
  if (leftSchool) {
    out.push(
      alert({
        id: `backpack-left-school-${leftSchool.to}`,
        fleetId: 'personal',
        kind: 'place',
        deviceId: 'backpack',
        placeId: 'school',
        title: "Maya's backpack left School",
        routine: true,
        detail: `After ${formatDuration(leftSchool.to - leftSchool.from)} at School.`,
        at: leftSchool.to,
      }),
    );
  }
  const backHome = [...backpack.stays].reverse().find((s) => s.placeId === 'home' && s.from > (leftSchool?.to ?? 0));
  if (backHome) {
    out.push(
      alert({
        id: `backpack-arrived-home-${backHome.from}`,
        fleetId: 'personal',
        kind: 'place',
        deviceId: 'backpack',
        placeId: 'home',
        title: "Maya's backpack arrived at Home",
        routine: true,
        detail: 'From Mission Library, Bartlett St.',
        at: backHome.from,
      }),
    );
  }

  out.push(
    alert({
      id: 'toolbox-offline',
      fleetId: 'personal',
      kind: 'device',
      deviceId: 'toolbox',
      title: 'Toolbox not seen for 6 hours',
      detail: 'Last seen near Garage 6 hours ago.',
      at: TOOLBOX_OFFLINE_AT + 6 * HOUR,
    }),
  );

  return out.sort((a, b) => b.at - a.at);
}

function acmeAlerts(devices: Record<string, Device>): Alert[] {
  const out: Alert[] = [];
  const survey = devices['survey-kit'];
  const leaving = survey.trips[survey.trips.length - 1];
  if (leaving) {
    out.push(
      alert({
        id: 'survey-left-dogpatch',
        fleetId: 'acme',
        kind: 'place',
        deviceId: 'survey-kit',
        placeId: 'dogpatch-site',
        title: 'Survey kit 04 left Dogpatch site',
        detail: 'Seen near Illinois St.',
        at: leaving.from,
      }),
    );
  }
  const van = devices['van-kit'];
  const atMission = lastStayAt(van, 'mission-site');
  if (atMission) {
    out.push(
      alert({
        id: `van-arrived-mission-${atMission.from}`,
        fleetId: 'acme',
        kind: 'place',
        deviceId: 'van-kit',
        placeId: 'mission-site',
        title: 'Van 12 kit arrived at 450 Mission site',
        routine: true,
        detail: 'From Yard.',
        at: atMission.from,
      }),
    );
  }
  const leftYard = lastStayAt(van, 'yard', true);
  if (leftYard) {
    out.push(
      alert({
        id: `van-left-yard-${leftYard.to}`,
        fleetId: 'acme',
        kind: 'place',
        deviceId: 'van-kit',
        placeId: 'yard',
        title: 'Van 12 kit left Yard',
        routine: true,
        detail: 'First departure of the day.',
        at: leftYard.to,
      }),
    );
  }
  return out.sort((a, b) => b.at - a.at);
}

// ─── Fleets ─────────────────────────────────────────────────────────

export const FLEETS: Fleet[] = [
  { id: 'personal', name: 'Personal', kind: 'personal', members: 2 },
  { id: 'acme', name: 'Acme Field Ops', kind: 'team', members: 6 },
];

export interface FleetData {
  fleet: Fleet;
  devices: Device[];
  places: Place[];
  alerts: Alert[];
  orders: Order[];
}

function ordersFor(fleetId: FleetId, devices: Device[]): Order[] {
  // Demo purchases grouped by pairing date. Each owned card is purchased once.
  const counts = new Map<number, number>();
  for (const device of devices) {
    const date = new Date(device.activatedAt);
    date.setHours(0, 0, 0, 0);
    const timestamp = date.getTime();
    counts.set(timestamp, (counts.get(timestamp) ?? 0) + 1);
  }
  return [...counts.entries()].sort(([a], [b]) => b - a).map(([date, cards], i) => ({
    id: `ORD-${fleetId === 'personal' ? 'P' : 'A'}-${String(i + 1).padStart(3, '0')}`,
    date,
    cards,
    amount: cards * Math.round(CARD_PRICE_AMOUNT * 100) / 100,
  }));
}

function buildFleet(fleet: Fleet, specs: DeviceSpec[], places: Place[]): FleetData {
  const devices = specs.map(simulate);
  const byId = Object.fromEntries(devices.map((d) => [d.id, d]));
  const alerts = fleet.id === 'personal' ? personalAlerts(byId) : acmeAlerts(byId);
  return { fleet, devices, places, alerts, orders: ordersFor(fleet.id, devices) };
}

export const FLEET_DATA: Record<FleetId, FleetData> = {
  personal: buildFleet(FLEETS[0], PERSONAL_SPECS, PERSONAL_PLACES),
  acme: buildFleet(FLEETS[1], ACME_SPECS, ACME_PLACES),
};

/** Seed credentials for the demo account (see services/auth/authService.ts). */
export const DEMO_ACCOUNT = {
  name: 'Sam Okafor',
  email: 'demo@latisent.com',
  password: 'trail-demo-2026',
};
